import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../../contexts/AuthContext';
import './ChatBot.css';
import API_URL from '../../config';

function ChatBot() {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const { getAuthHeader } = useAuth();
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const getGreeting = async () => {
      try {
        const response = await fetch(`${API_URL}/api/chatbot/greeting`);
        const data = await response.json();
        setMessages([
          {
            id: 1,
            sender: 'bot',
            text: data.greeting,
            timestamp: new Date()
          }
        ]);
      } catch (error) {
        console.error('Error getting greeting:', error);
        setMessages([
          {
            id: 1,
            sender: 'bot',
            text: "Hi! 👋 I'm here to help you reserve your stay.",
            timestamp: new Date()
          }
        ]);
      }
    };
    getGreeting();
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  const handleSendMessage = async (e) => {
    e.preventDefault();

    if (!input.trim() || loading) return;

    const userText = input;

    const userMessage = {
      id: messages.length + 1,
      sender: 'user',
      text: userText,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/chatbot/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeader()
        },
        body: JSON.stringify({
          message: userText
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to get response');
      }

      const botMessage = {
        id: messages.length + 2,
        sender: 'bot',
        text: data.response,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, botMessage]);

      if (data.reservationData) {
        const confirmMsg = {
          id: messages.length + 3,
          sender: 'bot',
          text: `✅ Perfect! Your reservation request has been submitted. We'll review it and send you a confirmation email soon to ${data.reservationData.email}`,
          timestamp: new Date()
        };
        setMessages(prev => [...prev, confirmMsg]);

        setTimeout(() => {
          fetch(`${API_URL}/api/chatbot/reset`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...getAuthHeader()
            }
          }).catch(err => console.error('Error resetting chat:', err));
        }, 2000);
      }
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = {
        id: messages.length + 2,
        sender: 'bot',
        text: '❌ Sorry, I encountered an error. Please try again.',
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMsg]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="floating floating-bottom-right chatbot-container">
      {isOpen && (
        <div className="floating-panel chatbot">
          <div className="chat-header">
            <h3>AI Booking Assistant</h3>
            <button className="chat-close" onClick={() => setIsOpen(false)}>×</button>
          </div>

          <div className="chat-messages">
            {messages.map(msg => (
              <div key={msg.id} className={`message ${msg.sender}`}>
                <div className="message-content">
                  {msg.sender === 'bot'
                    ? <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.text}</ReactMarkdown>
                    : msg.text
                  }
                </div>
                <div className="message-time">
                  {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            {loading && (
              <div className="message bot">
                <div className="message-content typing">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSendMessage} className="chat-input-form">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your response..."
              className="chat-input"
              disabled={loading}
            />
            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? '...' : 'Send'}
            </button>
          </form>
        </div>
      )}

      <button
        className={`chat-toggle ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Toggle chat"
      >
        {isOpen ? '×' : '💬'}
      </button>
    </div>
  );
}

export default ChatBot;